import { useQuery } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import { Button, Field, Input, RadioGroup, Select } from '@bb/ui';
import { useState } from 'react';
import { ApiError, type AdminApi, type PreviewProfile } from '../../../api/client.ts';

export type ProfileMode = 'random' | 'external_id' | 'test';

export interface ProfileSelectorProps {
  environmentId: string;
  /** The current choice; `null` while it is incomplete (no external ID typed, no test user chosen). */
  value: PreviewProfile | null;
  /** Every change of the choice. */
  onChange: (next: PreviewProfile | null) => void;
  disabled?: boolean;
}

const MODES: Array<{ value: ProfileMode; label: string }> = [
  { value: 'random', label: 'Random user' },
  { value: 'external_id', label: 'Specific user' },
  { value: 'test', label: 'Test user' },
];
export const DEFAULT_PREVIEW_SEED = 'preview';
const SEED_MAX = 64;

const modeOf = (value: PreviewProfile | null): ProfileMode => (value?.kind === 'external_id' ? 'external_id' : value?.kind === 'profile' ? 'test' : 'random');
const newSeed = () => Math.random().toString(36).slice(2, 10);

/** Members of the environment's content-test groups (BB-19), each profile once. */
export function useTestUsers(environmentId: string, enabled: boolean) {
  const { api } = useRouter().options.context as { api: AdminApi };
  return useQuery({
    queryKey: ['preview-test-users', environmentId],
    enabled,
    retry: false,
    queryFn: async () => {
      const groups = (await api.listInternalGroups(environmentId)).filter((g) => g.isContentTest);
      const lists = await Promise.all(groups.map(async (g) => (await api.listGroupMembers(environmentId, g.id)).map((m) => ({ ...m, group: g.name }))));
      return [...new Map(lists.flat().map((m) => [m.profileId, m])).values()];
    },
  });
}

/**
 * Who a preview is rendered for (BB-54): a random active user — the same seed picks the same user, so the preview
 * repeats —, a specific user by external ID, or a test user from a content-test internal group. Produces the
 * `profile` of a preview request.
 */
export function ProfileSelector({ environmentId, value, onChange, disabled = false }: ProfileSelectorProps) {
  const [mode, setMode] = useState<ProfileMode>(modeOf(value));
  const [seed, setSeed] = useState(value?.kind === 'random' ? value.seed : DEFAULT_PREVIEW_SEED);
  const [externalId, setExternalId] = useState(value?.kind === 'external_id' ? value.externalId : '');
  const [profileId, setProfileId] = useState(value?.kind === 'profile' ? value.profileId : '');
  const testUsers = useTestUsers(environmentId, mode === 'test');

  const emit = (next: { mode?: ProfileMode; seed?: string; externalId?: string; profileId?: string }) => {
    const m = next.mode ?? mode;
    if (m === 'random') onChange({ kind: 'random', seed: (next.seed ?? seed).trim() || DEFAULT_PREVIEW_SEED });
    else if (m === 'external_id') {
      const id = (next.externalId ?? externalId).trim();
      onChange(id ? { kind: 'external_id', externalId: id } : null);
    } else {
      const id = next.profileId ?? profileId;
      onChange(id ? { kind: 'profile', profileId: id } : null);
    }
  };

  const testUsersError =
    testUsers.error instanceof ApiError && testUsers.error.status === 403 ? 'Listing test users needs the profiles.view permission.' : testUsers.isError ? 'Test users could not be loaded.' : null;

  return (
    <fieldset className="profile-selector" disabled={disabled}>
      <legend>User</legend>
      <RadioGroup
        label="Preview as"
        options={MODES.map((m) => ({ ...m, disabled }))}
        value={mode}
        onValueChange={(next) => {
          setMode(next as ProfileMode);
          emit({ mode: next as ProfileMode });
        }}
      />
      {mode === 'random' && (
        <div className="inline-form">
          <Field label="Seed" hint="The same seed picks the same user, so the preview can be repeated.">
            <Input
              value={seed}
              maxLength={SEED_MAX}
              onChange={(e) => {
                setSeed(e.target.value);
                emit({ seed: e.target.value });
              }}
            />
          </Field>
          <Button
            size="sm"
            disabled={disabled}
            onClick={() => {
              const next = newSeed();
              setSeed(next);
              emit({ seed: next });
            }}
          >
            Another random user
          </Button>
        </div>
      )}
      {mode === 'external_id' && (
        <Field label="External ID" hint="The user ID your backend sends (external_id).">
          <Input
            value={externalId}
            maxLength={512}
            onChange={(e) => {
              setExternalId(e.target.value);
              emit({ externalId: e.target.value });
            }}
          />
        </Field>
      )}
      {mode === 'test' && (
        <>
          {testUsers.isPending && <p aria-busy="true">Loading test users…</p>}
          {testUsersError && <p role="alert">{testUsersError}</p>}
          {testUsers.data && testUsers.data.length === 0 && <p className="hint">No test users yet: add users to an internal group marked for content tests in Settings → Internal groups.</p>}
          {testUsers.data && testUsers.data.length > 0 && (
            <Field label="Choose a test user">
              <Select
                placeholder="Select…"
                options={testUsers.data.map((m) => ({ value: m.profileId, label: `${m.externalId ?? m.emailMasked ?? m.profileId} (${m.group})` }))}
                value={profileId}
                onChange={(e) => {
                  setProfileId(e.target.value);
                  emit({ profileId: e.target.value });
                }}
              />
            </Field>
          )}
        </>
      )}
    </fieldset>
  );
}
